<template>
  <div v-if="generalFeedback.length" class="px-5 @2xl:px-7 py-2.5 first:pt-3 last:pb-3">
    <ul>
      <li v-for="taskFeedback in generalFeedback" :key="taskFeedback.id">
        <s-latex :content="taskFeedback.content" />
      </li>
    </ul>
  </div>
  <template v-for="feedbackRow in feedbackAreaRows" :key="feedbackRow.key">
    <div
      v-if="feedbackRow.pointsAvailable || feedbackRow.feedbacks.length"
      class="px-5 @2xl:px-7 py-2.5 first:pt-3 last:pb-3"
    >
      <div class="flex items-center gap-4">
        <h3 class="font-bold flex-1">
          <task-text :text="feedbackRow.label" :task-state="taskState" />
        </h3>
        <s-badge
          v-if="feedbackRow.pointsAvailable"
          size="sm"
          :color="
            badgeColor({
              pointsEarned: feedbackRow.pointsEarned,
              pointsAvailable: feedbackRow.pointsAvailable,
            })
          "
        >
          {{ formatFraction(feedbackRow.pointsEarned, feedbackRow.pointsAvailable) }}
        </s-badge>
      </div>
      <ul v-if="feedbackRow.feedbacks.length">
        <li v-for="taskFeedback in feedbackRow.feedbacks" :key="taskFeedback.id">
          <div>
            <task-text :text="taskFeedback.content" :task-state="taskState" />
          </div>
        </li>
      </ul>
    </div>
  </template>
</template>
<script setup lang="ts">
import Rubric = App.DTOs.Rubrics.Rubric;
import {TaskFeedback, TaskState} from '../../../types/entities/tasks';
import {computed} from 'vue';
import TaskCriterionGradeDto = App.DTOs.Entities.Grades.TaskCriterionGradeDto;
import PartRubric = App.DTOs.Rubrics.PartRubric;
import SBadge from '../../../design-system/SBadge.vue';
import TaskText from '../../../text/TaskText.vue';
import {formatFraction} from '../../../format/grades';
import {scaleGradeToPoints} from '../../../util/grades';
import SLatex from '../../../components/SLatex.vue';

const {rubric, feedbackByParts, grades} = defineProps<{
  rubric: Rubric;
  feedbackByParts: Record<string, TaskFeedback[]>;
  grades: TaskCriterionGradeDto[];
  taskState: TaskState;
}>();

const allFeedbacks = computed(() =>
  Object.keys(feedbackByParts).reduce(
    (acc, key) => [...acc, ...feedbackByParts[key]],
    [] as TaskFeedback[]
  )
);

/**
 * Return true if Grade is for the given PartRubric.
 *
 * @param grade
 * @param partRubric
 */
function gradeForPart(grade: TaskCriterionGradeDto, partRubric: PartRubric) {
  if (!grade.part && !grade.blockId) {
    return false;
  }

  if (grade.blockId) {
    return grade.blockId === partRubric.blockId && grade.criterionName === partRubric.criterionName;
  }

  return grade.part === partRubric.partName;
}

/**
 * Return true if Feedback is for the given PartRubric.
 *
 * @param feedback
 * @param partRubric
 */
function feedbacksForPart(feedback: TaskFeedback, partRubric: PartRubric) {
  if (!feedback.partName && !feedback.blockId) {
    return false;
  }

  if (feedback.blockId) {
    return (
      feedback.blockId === partRubric.blockId && feedback.criterionName === partRubric.criterionName
    );
  }

  return feedback.partName === partRubric.partName;
}

/**
 * Array of general feedback (i.e. not tied to a specific Block/Criteria).
 */
const generalFeedback = computed(() =>
  allFeedbacks.value.filter((feedback) => {
    return !feedback.partName && !feedback.blockId;
  })
);

/**
 * Array of grades/feedbacks for each part of the rubric.
 */
const feedbackAreaRows = computed(() =>
  rubric.parts.map((partRubric) => {
    const pointsAvailable = partRubric.pointsAvailable || 0;

    const grade = grades.find((grade) => gradeForPart(grade, partRubric));
    const pointsEarned = scaleGradeToPoints(grade, pointsAvailable);

    return {
      key: `${partRubric.blockId}_${partRubric.criterionName}`,
      label: partRubric.label,
      pointsAvailable,
      pointsEarned,
      feedbacks: allFeedbacks.value.filter((feedback) => feedbacksForPart(feedback, partRubric)),
    };
  })
);

/**
 * Return the color of the badge based on the points earned and available.
 *
 * @param pointsEarned
 * @param pointsAvailable
 */
const badgeColor = ({
  pointsEarned,
  pointsAvailable,
}: Pick<TaskCriterionGradeDto, 'pointsEarned' | 'pointsAvailable'>) => {
  if (pointsEarned === pointsAvailable) {
    return 'green';
  }

  if (pointsEarned > 0) {
    return 'blue';
  }

  return 'red';
};
</script>
